"use client";

import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useState } from "react";
import { isPushSupported, subscribeToPush } from "@/lib/push/subscribe-client";

/**
 * One-time web-push opt-in shown at a retention value moment — right after a
 * seeker confirms they applied ("get a ping when the employer replies?") or
 * on a second same-session return via an email link ("get notified about new
 * matches?"). Distinct from the settings/seeker `PushOptIn` toggle: this is a
 * contextual, dismissible CTA that never re-asks for 14 days once dismissed,
 * so it can't become nag — the dismiss key is shared across every call site,
 * so dismissing it anywhere suppresses it everywhere for the same window.
 *
 * Renders nothing unless push is supported, the flag is on, permission isn't
 * already denied, the user isn't already subscribed, and it wasn't dismissed
 * in the last 14 days — so it only ever appears when it can add value.
 *
 * `Notification.requestPermission()` is ONLY ever called from `enable()`,
 * i.e. in direct response to the user clicking "Notify me" on this card —
 * never on mount/appearance. Never wire a call to `subscribeToPush()` (or
 * `Notification.requestPermission()` directly) into an effect that runs
 * before the user has accepted the card.
 *
 * Gated by the caller on the server-resolved `web-push` flag (same flag that
 * gates the whole push surface); do not render this without it.
 */
const DISMISS_KEY = "post-apply-push-dismissed-at";
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

type PromptState = "checking" | "hidden" | "idle" | "working" | "subscribed";
type PromptVariant = "applied" | "engaged";

const COPY: Record<
	PromptVariant,
	{
		question: { en: string; id: string };
		subscribed: { en: string; id: string };
	}
> = {
	// Shown right after the seeker marks a job "applied".
	applied: {
		question: {
			en: "Get a ping when the employer replies?",
			id: "Dikabari kalau employer balas?",
		},
		subscribed: {
			en: "✓ We'll ping you if the employer replies.",
			id: "✓ Kami kabari kalau employer balas.",
		},
	},
	// Shown on a second same-session return from an email link — the seeker
	// hasn't necessarily applied to anything yet, so the copy is generic.
	engaged: {
		question: {
			en: "Get notified about new matching jobs?",
			id: "Dikabari kalau ada lowongan baru yang cocok?",
		},
		subscribed: {
			en: "✓ We'll ping you about new matches.",
			id: "✓ Kami kabari kalau ada lowongan cocok baru.",
		},
	},
};

function dismissedWithinWindow(nowMs: number): boolean {
	try {
		const raw = window.localStorage.getItem(DISMISS_KEY);
		if (!raw) return false;
		const at = Number.parseInt(raw, 10);
		return Number.isFinite(at) && nowMs - at < DISMISS_WINDOW_MS;
	} catch {
		return false;
	}
}

export function PostApplyPushPrompt({
	locale,
	source = "post_apply",
	variant = "applied",
}: {
	locale: "en" | "id";
	source?: string;
	/** Which copy to show — "applied" (default, post-apply) or "engaged" (a
	 * 2nd-visit-from-email value moment, no apply implied). */
	variant?: PromptVariant;
}) {
	const isEn = locale === "en";
	const copy = COPY[variant];
	const posthog = usePostHog();
	const [state, setState] = useState<PromptState>("checking");

	useEffect(() => {
		if (!isPushSupported()) {
			setState("hidden");
			return;
		}
		if (
			Notification.permission === "denied" ||
			dismissedWithinWindow(Date.now())
		) {
			setState("hidden");
			return;
		}
		navigator.serviceWorker
			.getRegistration()
			.then(async (reg) => {
				const sub = reg ? await reg.pushManager.getSubscription() : null;
				// Already subscribed → no value in prompting again.
				setState(sub ? "hidden" : "idle");
			})
			.catch(() => setState("idle"));
	}, []);

	// Fire the shown event once, only when the prompt actually becomes visible.
	useEffect(() => {
		if (state === "idle") {
			posthog?.capture?.("push_prompt_shown", { source });
		}
	}, [state, posthog, source]);

	const enable = useCallback(async () => {
		setState("working");
		const result = await subscribeToPush(locale);
		if (result === "subscribed") {
			posthog?.capture?.("push_subscribed", { source });
			setState("subscribed");
			return;
		}
		if (result === "denied") {
			posthog?.capture?.("push_permission_denied", { source });
			setState("hidden");
			return;
		}
		// dismissed_permission | error → let the user try again this visit.
		setState("idle");
	}, [posthog, source, locale]);

	const dismiss = useCallback(() => {
		posthog?.capture?.("push_prompt_dismissed", { source });
		try {
			window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
		} catch {
			// Best-effort persist; the in-memory hide below still holds this visit.
		}
		setState("hidden");
	}, [posthog, source]);

	if (state === "checking" || state === "hidden") return null;

	if (state === "subscribed") {
		return (
			<div className="mt-2 rounded-lg border border-border/60 bg-muted/40 px-2 py-1.5 text-center text-[11px] font-medium text-muted-foreground">
				{isEn ? copy.subscribed.en : copy.subscribed.id}
			</div>
		);
	}

	return (
		<div className="mt-2 rounded-lg border border-border/60 bg-muted/40 p-2 text-xs">
			<p className="mb-1.5 font-semibold text-foreground/80">
				{isEn ? copy.question.en : copy.question.id}
			</p>
			<div className="flex flex-wrap gap-1.5">
				<button
					type="button"
					onClick={enable}
					disabled={state === "working"}
					className="rounded bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				>
					{state === "working"
						? isEn
							? "Enabling…"
							: "Mengaktifkan…"
						: isEn
							? "Notify me"
							: "Kabari saya"}
				</button>
				<button
					type="button"
					onClick={dismiss}
					disabled={state === "working"}
					className="rounded border border-border/80 bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				>
					{isEn ? "No thanks" : "Tidak"}
				</button>
			</div>
		</div>
	);
}
